/*
 * -----------------------------------------------------------------------
 *  <copyright file="step4-overview/index.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import FormControl from '@mui/material/FormControl';
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import cn from 'clsx';
import React, { ChangeEvent, forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import 'views/components/central-monitoring/panels/add-panel/styles.scss';
import { Errors } from '@components/central-monitoring/panels/add-panel/interfaces';
import AddPanelColumn from '@components/central-monitoring/panels/add-panel/step4-overview/add-panel-column';
import {
    IStudyOverviewExtraColumn,
    Step4OverviewProps,
} from '@components/central-monitoring/panels/add-panel/step4-overview/step4-overview.interface';
import CharactersRemaining from '@components/core/characters-remaining';
import OutlinedWrapper from '@components/core/outlined-wrapper';
import SelectWithCategories from '@components/core/select-with-categories';
import TextField from '@components/core/text-field';
import { STEP4_COLUMN_INITIAL, STEP4_INITIAL_ERRORS, STEP4_NUMBER_OF_COLUMNS } from './step4-overview.data';
import { StudyOverviewExtraColumnType } from 'types/graphql-global-types';
import { useTranslation } from '@hooks/index';
import { notEmpty } from '@utils/utility-helpers';
import { validateFields } from '@utils/validation';
import './styles.scss';

const Step4Overview = forwardRef(
    ({ show, canSave, panelState, updateState, updateErrorState }: Step4OverviewProps, ref) => {
        const { t } = useTranslation();

        const [columnsNumber, setColumnsNumber] = useState<number>(panelState.studyOverviewExtraColumns?.length || 0);
        const [errors, setErrors] = useState<Errors>({ ...STEP4_INITIAL_ERRORS });

        useEffect(() => {
            const stateColumnsNumber = panelState.studyOverviewExtraColumns?.length || 0;
            if (columnsNumber && stateColumnsNumber < columnsNumber) {
                const columns = panelState.studyOverviewExtraColumns?.map((column) => ({ ...column })) || [];
                for (let i = stateColumnsNumber; i < columnsNumber; i++) {
                    columns.push({ ...STEP4_COLUMN_INITIAL });
                }
                updateState({
                    target: {
                        name: 'studyOverviewExtraColumns',
                        value: columns,
                    },
                });
            }
            if (columnsNumber < stateColumnsNumber) {
                const columns = panelState.studyOverviewExtraColumns?.map((column) => ({ ...column })) || [];
                columns.splice(columnsNumber, 1);
                updateState({
                    target: {
                        name: 'studyOverviewExtraColumns',
                        value: columns,
                    },
                });
            }
            if (!columnsNumber) {
                updateState({ target: { name: 'studyOverviewExtraColumns', value: [] } });
            }
            setErrors({ ...STEP4_INITIAL_ERRORS });
        }, [columnsNumber]);

        useEffect(() => {
            updateErrorState(errors, 4);
        }, [errors]);

        const onColumnsNumberChange = () => (event: DropDownListChangeEvent) => setColumnsNumber(+event.target.value);

        const onChangeYAxisColumnValue = (event: ChangeEvent<HTMLInputElement>) => {
            const { value } = event.target;

            updateState({
                target: {
                    name: 'yAxisColumnValue',
                    value,
                },
            });

            const yAxisColumnValueError: Errors = validateFields(value, 'yAxisColumnValue', 50);

            setErrors({
                ...errors,
                ...yAxisColumnValueError,
            });
        };

        const updateColumn = (data: IStudyOverviewExtraColumn, index: number) => {
            const updatedState = panelState.studyOverviewExtraColumns?.map((column) => ({ ...column }));
            if (updatedState) {
                updatedState[index] = data;
                updateState({ target: { name: 'studyOverviewExtraColumns', value: updatedState } });
            }
        };

        const checkRequiredFields = (reqFields: string[]) => {
            let isValid = true;
            let newErrors = { ...errors };
            if (columnsNumber) {
                panelState.studyOverviewExtraColumns
                    ?.filter(notEmpty)
                    .forEach((column: IStudyOverviewExtraColumn, index) => {
                        reqFields.forEach((field) => {
                            let value = column[field];
                            if (typeof value === 'number') value = `${value}`;

                            const error: Errors = validateFields(value, field, 0, true);

                            newErrors = {
                                ...newErrors,
                                [`${field}${index}`]: error[field],
                            };
                        });
                        const isRate = column?.type === StudyOverviewExtraColumnType.RATE;
                        const numeratorError: Errors = validateFields(
                            column.numeratorField ?? '',
                            'numerator',
                            0,
                            isRate,
                        );
                        const denominatorError: Errors = validateFields(
                            column.denominatorField ?? '',
                            'denominator',
                            0,
                            isRate,
                        );
                        const dataFieldError: Errors = validateFields(
                            column.dataField ?? undefined,
                            'dataField',
                            0,
                            !isRate,
                        );

                        newErrors = {
                            ...newErrors,
                            [`numeratorField${index}`]: numeratorError.numerator,
                            [`denominatorField${index}`]: denominatorError.denominator,
                            [`dataField${index}`]: dataFieldError.dataField,
                        };
                    });
            }

            if (Object.keys(newErrors).filter((item) => (newErrors as any)[item]).length > 0) isValid = false;

            setErrors({ ...newErrors });
            return isValid;
        };

        const validationOnUpdate = () => {
            const requiredFields = ['label', 'type', 'dataSource'];

            return checkRequiredFields(requiredFields);
        };

        useImperativeHandle(ref, () => ({
            validationOnUpdate() {
                return validationOnUpdate();
            },
        }));

        return (
            <div className={cn('step4', { step4__hidden: !show })}>
                <div className="type-edc-wrapper mt">
                    <FormControl variant="outlined" className="form-control-type not-full mr">
                        <TextField
                            label={t('admin:yAxisColumnValue')}
                            id="add-panel-yAxisColumnValue"
                            fullWidth
                            value={panelState.yAxisColumnValue}
                            onChange={onChangeYAxisColumnValue}
                            placeholder={t('admin:enterYAxisColumnValue')}
                            helperText={
                                <CharactersRemaining
                                    id="add-panel-yAxisColumnValue-help-text"
                                    length={panelState.yAxisColumnValue?.length}
                                    max={50}
                                    error={errors.yAxisColumnValue}
                                />
                            }
                            error={!!errors.yAxisColumnValue}
                            variant="outlined"
                            color="secondary"
                            disabled={!canSave}
                        />
                    </FormControl>
                </div>
                <OutlinedWrapper customClass="mb" label={t('admin:additionalColumns')}>
                    <div className="type-edc-wrapper mt-10">
                        <FormControl variant="outlined" className="form-control-type not-full">
                            <SelectWithCategories
                                label={t('admin:numberOfColumnsR')}
                                dataArray={STEP4_NUMBER_OF_COLUMNS}
                                onChange={onColumnsNumberChange}
                                fieldSelect="field"
                                currentState={columnsNumber}
                                initialState="default"
                                labelField="panelType"
                                showWarning={false}
                                filterable={false}
                                customClassName="add-panel-column-number"
                            />
                        </FormControl>
                    </div>
                    {!!columnsNumber &&
                        !!panelState.studyOverviewExtraColumns?.length &&
                        panelState.studyOverviewExtraColumns
                            .filter(notEmpty)
                            .map((column, index) => (
                                <AddPanelColumn
                                    key={`panel-column-${column?.columnId || index}`}
                                    column={column}
                                    canSave={canSave}
                                    index={index}
                                    updateColumn={updateColumn}
                                    errors={errors}
                                    setErrors={setErrors}
                                />
                            ))}
                </OutlinedWrapper>
            </div>
        );
    },
);

export default Step4Overview;
